import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
} from "@mui/material";
import { CheckCircle, Cancel, Inventory } from "@mui/icons-material";
import "./index.scss";

const VariantSelectionModal = ({ open, onClose, onConfirm, productDetail }) => {
  const [selectedVariant, setSelectedVariant] = useState(null);

  if (!productDetail || !productDetail.variants) return null;

  const handleVariantChange = (event) => {
    const variantId = event.target.value;
    const variant = productDetail.variants.find(
      (v) => v.variant_id === variantId
    );
    setSelectedVariant(variant);
  };

  const handleConfirm = () => {
    if (selectedVariant) {
      onConfirm(selectedVariant);
    }
  };

  const getVariantPrice = (variant) => {
    if (!variant?.variant_detail) return "N/A";
    const variantDetail = variant.variant_detail;
    return (
      variantDetail.discounted_price || variantDetail.original_price || "N/A"
    );
  };

  const isVariantInStock = (variant) => {
    return variant?.variant_detail?.sold_out === false;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="b2b-variant-selection-modal"
      PaperProps={{
        className: "b2b-modal-paper",
      }}
    >
      <DialogTitle className="b2b-modal-header">
        <Box className="b2b-header-content">
          <Inventory color="primary" />
          <Typography variant="h5" component="div" className="b2b-modal-title">
            Select Product Variant
          </Typography>
        </Box>
        <Typography variant="body2" className="b2b-modal-subtitle">
          Choose from available product options
        </Typography>
      </DialogTitle>

      <DialogContent className="b2b-modal-content">
        <Typography variant="h6" className="b2b-product-title">
          {productDetail.title}
        </Typography>

        <RadioGroup
          value={selectedVariant?.variant_id || ""}
          onChange={handleVariantChange}
          className="b2b-variants-list"
        >
          {productDetail.variants.map((variant, index) => (
            <Card
              key={variant.variant_id}
              className={`b2b-variant-card ${
                selectedVariant?.variant_id === variant.variant_id
                  ? "b2b-selected"
                  : ""
              }`}
            >
              <CardContent>
                <FormControlLabel
                  value={variant.variant_id}
                  control={<Radio />}
                  disabled={!isVariantInStock(variant)}
                  className="b2b-variant-radio"
                  label={
                    <Box className="b2b-variant-content">
                      <Box className="b2b-variant-header">
                        <Typography
                          variant="h6"
                          component="div"
                          className="b2b-variant-title"
                        >
                          {variant.title}
                        </Typography>
                        <Typography variant="h6" className="b2b-variant-price">
                          PKR {getVariantPrice(variant)}
                        </Typography>
                      </Box>

                      <Box className="b2b-variant-options">
                        {variant.option1 && variant.option1 !== "Default" && (
                          <Chip
                            label={variant.option1}
                            size="small"
                            variant="outlined"
                            className="b2b-option-chip"
                          />
                        )}
                        {variant.option2 && (
                          <Chip
                            label={variant.option2}
                            size="small"
                            variant="outlined"
                            className="b2b-option-chip"
                          />
                        )}
                        {variant.option3 && (
                          <Chip
                            label={variant.option3}
                            size="small"
                            variant="outlined"
                            className="b2b-option-chip"
                          />
                        )}
                      </Box>

                      <Box className="b2b-variant-meta">
                        <Box className="b2b-variant-status">
                          {isVariantInStock(variant) ? (
                            <Chip
                              label="In Stock"
                              color="success"
                              size="small"
                              className="b2b-status-chip"
                            />
                          ) : (
                            <Chip
                              label="Out of Stock"
                              color="error"
                              size="small"
                              className="b2b-status-chip"
                            />
                          )}
                          {variant.sku && (
                            <Chip
                              label={`SKU: ${variant.sku}`}
                              size="small"
                              variant="outlined"
                              className="b2b-sku-chip"
                            />
                          )}
                        </Box>

                        {variant.weight && (
                          <Typography
                            variant="body2"
                            className="b2b-weight-info"
                          >
                            Weight: {variant.weight} {variant.weight_unit}
                          </Typography>
                        )}
                      </Box>

                      {/* Show installment info if available */}
                      {variant.variant_detail?.commission_installment_plans && (
                        <Box className="b2b-installment-info">
                          <Typography
                            variant="body2"
                            className="b2b-installment-text"
                          >
                            Installments available:{" "}
                            {
                              variant.variant_detail
                                .commission_installment_plans.as_low_as
                            }
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  }
                />
              </CardContent>
            </Card>
          ))}
        </RadioGroup>

        {productDetail.variants.length === 0 && (
          <Typography className="b2b-no-variants-message">
            No variants available for this product
          </Typography>
        )}
      </DialogContent>

      <DialogActions className="b2b-modal-actions">
        <Button
          onClick={onClose}
          variant="outlined"
          color="secondary"
          startIcon={<Cancel />}
          className="b2b-cancel-button"
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          startIcon={<CheckCircle />}
          disabled={!selectedVariant || !isVariantInStock(selectedVariant)}
          className="b2b-confirm-button"
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VariantSelectionModal;
